import { getWorkflowConfigurationTriggerIndexFromTriggerKey } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/useWorkflowGraph.helpers";
import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import {
  getWorkflowTaskIconFromType,
  getWorkflowTaskNameFromType,
  getWorkflowTriggerIconFromTrigger,
  getWorkflowTriggerNameFromTrigger,
} from "#src/components/Workflows/WorkflowsHelpers";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import {
  Accordion,
  Button,
  ButtonToggleGroup,
  ButtonWithPopover,
  Drawer,
  DrawerDescriptorWithIconProps,
  DrawerProps,
  Icon,
  IconVariants,
  Pill,
} from "@validereinc/common-components";
import {
  isTriggerAssetCreate,
  isTriggerAssetUpdate,
  isTriggerCron,
  isTriggerFormSubmissionCreate,
  isTriggerOtherWorkflowCompletion,
  SystemActionType,
  SystemActionTypeType,
  UserTaskType,
  WorkflowStepType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import { isFiniteNumber } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { WorkflowConfigurationDrawerAssignmentFilterDetails } from "./Components/WorkflowConfigurationDrawerAssignmentFilterDetails";
import WorkflowConfigurationDrawerFinishedDetails from "./Components/WorkflowConfigurationDrawerFinishedDetails";
import WorkflowConfigurationDrawerTaskDetails from "./Components/WorkflowConfigurationDrawerTaskDetails";
import WorkflowConfigurationDrawerTriggerDetails from "./Components/WorkflowConfigurationDrawerTriggerDetails";
import { WorkflowConfigurationAssessTemplatedReportForComments } from "./Forms/WorkflowConfigurationAssessTemplatedReportForCommentsDetails";
import WorkflowConfigurationAssetCreateUpdate from "./Forms/WorkflowConfigurationAssetCreateUpdate";
import WorkflowConfigurationAssignee from "./Forms/WorkflowConfigurationAssignee";
import WorkflowConfigurationBaseInformation from "./Forms/WorkflowConfigurationBaseInformation";
import WorkflowConfigurationChoice from "./Forms/WorkflowConfigurationChoice";
import WorkflowConfigurationDelayDetails from "./Forms/WorkflowConfigurationDelayDetails";
import WorkflowConfigurationEventDetails from "./Forms/WorkflowConfigurationEventDetails";
import WorkflowConfigurationFormChoiceDetails from "./Forms/WorkflowConfigurationFormChoiceDetails";
import WorkflowConfigurationFormCreationDetails from "./Forms/WorkflowConfigurationFormCreationDetails";
import WorkflowConfigurationInputVariableForm from "./Forms/WorkflowConfigurationInputVariableForm";
import WorkflowConfigurationLockReportDetails from "./Forms/WorkflowConfigurationLockReportDetails";
import WorkflowConfigurationOutputVariableForm from "./Forms/WorkflowConfigurationOutputVariableForm";
import WorkflowConfigurationRunCalculationsDetails from "./Forms/WorkflowConfigurationRunCalculationsDetails";
import WorkflowConfigurationRunDRVCDetails from "./Forms/WorkflowConfigurationRunDRVCDetails";
import WorkflowConfigurationRunTemplatedReportDetails from "./Forms/WorkflowConfigurationRunTemplatedReportDetails";
import WorkflowConfigurationRunWorkerDetails from "./Forms/WorkflowConfigurationRunWorkerDetails";
import WorkflowScheduledTriggerForm from "./Forms/WorkflowConfigurationScheduledTriggerForm";
import WorkflowConfigurationSendNotificationDetails from "./Forms/WorkflowConfigurationSendNotificationDetails";
import WorkflowConfigurationSendReportDetails from "./Forms/WorkflowConfigurationSendReportDetails";
import WorkflowConfigurationSubmitForm from "./Forms/WorkflowConfigurationSubmitForm";
import WorkflowConfigurationTriggerDueDate from "./Forms/WorkflowConfigurationTriggerDueDate";
import WorkflowConfigurationTriggerFormCreation from "./Forms/WorkflowConfigurationTriggerFormCreation";
import WorkflowConfigurationTriggerWorkflowCompletion from "./Forms/WorkflowConfigurationTriggerWorkflowCompletion";
import WorkflowConfigurationUpdateAttributesDetails from "./Forms/WorkflowConfigurationUpdateAttributesDetails";
import WorkflowConfigurationUpdateRecordStatusDetails from "./Forms/WorkflowConfigurationUpdateRecordStatusDetails";
import WorkflowConfigurationVariableChoiceDetails from "./Forms/WorkflowConfigurationVariableChoiceDetails";
import { WorkflowConfigurationDrawerProps } from "./types";
import styles from "./WorkflowConfigurationDrawer.module.scss";
import { useWorkflowConfigurationFormStore } from "./WorkflowConfigurationFormStore";
import { WorkflowConfigurationPluginOptions } from "./Forms/WorkflowConfigurationPluginOptions";
import { WorkflowConfigurationDialog } from "./Components/WorkflowConfigurationDialog";

const { getAllWorkflowSteps } = Workflows.logic;

const cx = classNames.bind(styles);

type WorkflowTemplateEditTabsType = "details" | "assignees" | "advanced";

const WorkflowConfigurationDrawer = ({
  onClose,
  selectedNode,
  workflowTemplate,
}: WorkflowConfigurationDrawerProps) => {
  const selectedNodeId = selectedNode?.id ?? "";

  const isFinishNode = selectedNode?.data.variant === "finish";
  const stepDefinition = getAllWorkflowSteps(workflowTemplate?.config?.steps)?.[
    selectedNodeId
  ];
  const isStepUserTask =
    !!stepDefinition && stepDefinition?.type === WorkflowStepType.USER_TASK;
  const isStepSystemAction =
    !!stepDefinition && stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION;

  //TODO: support parallel steps
  const isStepParallel =
    !!stepDefinition && stepDefinition?.type === WorkflowStepType.PARALLEL;

  const { localize } = useLocalization();
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] =
    useState(false);

  const [activeTab, setActiveTab] =
    useState<WorkflowTemplateEditTabsType>("details");

  const shouldHaveAutomationVariant =
    stepDefinition?.type === "system_action" ||
    selectedNode?.data.variant === "finish";

  const isTrigger = selectedNode?.data.variant === "trigger";

  const [isPluginEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:plugins",
  });

  const shouldRenderInputOutputConfig =
    isStepUserTask && stepDefinition?.task.type === UserTaskType.SUBMIT_FORM;
  const shouldRenderAdvancedTab =
    isPluginEnabled || shouldRenderInputOutputConfig;

  const contentSwitcherItems = useMemo(() => {
    const output: Array<{
      label: string;
      dataKey: WorkflowTemplateEditTabsType;
    }> = [
      { label: "Details", dataKey: "details" },
      { label: "Assignees", dataKey: "assignees" },
    ];

    if (shouldRenderAdvancedTab) {
      output.push({ label: "Advanced", dataKey: "advanced" });
    }

    return output;
  }, [shouldRenderAdvancedTab]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const triggerDefinition = useMemo(() => {
    if (selectedNode?.data.variant !== "trigger") return;

    const triggerIndex = getWorkflowConfigurationTriggerIndexFromTriggerKey(
      selectedNode.id
    );
    if (workflowTemplate && isFiniteNumber(triggerIndex)) {
      return workflowTemplate?.triggers?.[triggerIndex];
    }
    return;
  }, [selectedNode, workflowTemplate]);

  const isStepTrigger = !!triggerDefinition;

  const formSubmissionCreateTriggerFormSchemaId =
    triggerDefinition && isTriggerFormSubmissionCreate(triggerDefinition)
      ? triggerDefinition.form_schema_id
      : "";

  const formSchemaQuery = useGetOneFormSchema(
    {
      id: formSubmissionCreateTriggerFormSchemaId,
    },
    { enabled: !!formSubmissionCreateTriggerFormSchemaId }
  );

  // Edit Logic:
  const [canEditWorkflowTemplate] = useIsFeatureAvailable({
    featureFlagQuery: "core:workflows",
    permissionQuery: "workflow_templates:write",
  });

  const {
    isNodeDirty,
    isInEditMode,
    updateBaseInformationWithHistory,
    updateUserTaskAssigneeWithHistory,
    updateUserTaskSubmitFormWithHistory,
    updateUserTaskChoiceWithHistory,
    revertTaskChanges,
    updateTriggerDueDateWithHistory,
    updateTriggerWorkflowCompletionWithHistory,
    updateFormCreationTriggerWithHistory,
    updateAssetCreateAndUpdateTriggerWithHistory,
    updateScheduledTriggerWithHistory,
    updateDelayWithHistory,
    updateCreateEventWithHistory,
    updateLockReportWithHistory,
    updateUpdateAttributesWithHistory,
    updateInputWithHistory,
    updateOutputWithHistory,
    updatePluginWithHistory,
    updateFormChoiceAutomationWithHistory,
    updateVariableChoiceAutomationWithHistory,
    updateRunTemplatedReportWithHistory,
    updateRunWorkerWithHistory,
    updateRunDRVCWithHistory,
    updateRunCalculationsWithHistory,
    updateUpdateRecordStatusWithHistory,
    updateAssessTemplatedReportForCommentsWithHistory,
    updateFormCreationAutomationWithHistory,
    updateSendNotificationWithHistory,
    updateSendReportWithHistory,
  } = useWorkflowGraphState();

  const { initializeFormValues, resetFormValues } =
    useWorkflowConfigurationFormStore();

  // Initialize form values when drawer opens or node changes
  useEffect(() => {
    setActiveTab("details");
    if (selectedNode) {
      //TODO: initialize parallel tasks
      initializeFormValues(stepDefinition, triggerDefinition);
    }

    // Reset form values when drawer closes
    return () => {
      if (!selectedNode) {
        resetFormValues();
      }
    };
  }, [
    selectedNode?.id,
    initializeFormValues,
    resetFormValues,
    isStepUserTask,
    stepDefinition,
    triggerDefinition,
  ]);

  const baseInformationIsValid = useWorkflowConfigurationFormStore(
    (state) => state.baseInformationIsValid
  );

  const submitFormIsValid = useWorkflowConfigurationFormStore(
    (state) => state.submitFormIsValid
  );

  const choiceIsValid = useWorkflowConfigurationFormStore(
    (state) => state.choiceIsValid
  );

  const assigneeIsValid = useWorkflowConfigurationFormStore(
    (state) => state.assigneeIsValid
  );

  const triggerCronIsValid = useWorkflowConfigurationFormStore(
    (state) => state.triggerCronIsValid
  );

  const triggerDueDateIsValid = useWorkflowConfigurationFormStore(
    (state) => state.triggerDueDateIsValid
  );

  const triggerWorkflowCompletionIsValid = useWorkflowConfigurationFormStore(
    (state) => state.triggerWorkflowCompletionIsValid
  );

  const triggerFormCreationIsValid = useWorkflowConfigurationFormStore(
    (state) => state.triggerFormCreationIsValid
  );

  const triggerAssetIsValid = useWorkflowConfigurationFormStore(
    (state) => state.triggerAssetIsValid
  );

  const delayIsValid = useWorkflowConfigurationFormStore(
    (state) => state.delayIsValid
  );

  const createEventIsValid = useWorkflowConfigurationFormStore(
    (state) => state.createEventIsValid
  );

  const updateAttributesIsValid = useWorkflowConfigurationFormStore(
    (state) => state.updateAttributesIsValid
  );

  const runTemplatedReportIsValid = useWorkflowConfigurationFormStore(
    (state) => state.runTemplatedReportIsValid
  );

  const lockReportIsValid = useWorkflowConfigurationFormStore(
    (state) => state.lockReportIsValid
  );

  const formChoiceIsValid = useWorkflowConfigurationFormStore(
    (state) => state.formChoiceIsValid
  );

  const variableChoiceIsValid = useWorkflowConfigurationFormStore(
    (state) => state.variableChoiceIsValid
  );

  const updateRecordStatusIsValid = useWorkflowConfigurationFormStore(
    (state) => state.updateRecordStatusIsValid
  );

  const runDRVCIsValid = useWorkflowConfigurationFormStore(
    (state) => state.runDRVCIsValid
  );

  const runCalculationsIsValid = useWorkflowConfigurationFormStore(
    (state) => state.runCalculationsIsValid
  );

  const assessTemplatedReportForCommentsIsValid =
    useWorkflowConfigurationFormStore(
      (state) => state.assessTemplatedReportForCommentsIsValid
    );

  const sendNotificationIsValid = useWorkflowConfigurationFormStore(
    (state) => state.sendNotificationIsValid
  );

  const sendReportIsValid = useWorkflowConfigurationFormStore(
    (state) => state.sendReportIsValid
  );

  const runWorkerIsValid = useWorkflowConfigurationFormStore(
    (state) => state.runWorkerIsValid
  );

  const formCreationIsValid = useWorkflowConfigurationFormStore(
    (state) => state.formCreationIsValid
  );

  const areAllFormsValid = useMemo(() => {
    // For triggers, we need to check trigger-specific validations
    if (isTrigger && triggerDefinition) {
      if (isTriggerCron(triggerDefinition)) {
        return triggerCronIsValid && triggerDueDateIsValid;
      }

      if (isTriggerOtherWorkflowCompletion(triggerDefinition)) {
        return triggerWorkflowCompletionIsValid && triggerDueDateIsValid;
      }

      if (isTriggerFormSubmissionCreate(triggerDefinition)) {
        return triggerFormCreationIsValid && triggerDueDateIsValid;
      }

      if (
        isTriggerAssetCreate(triggerDefinition) ||
        isTriggerAssetUpdate(triggerDefinition)
      ) {
        return triggerAssetIsValid && triggerDueDateIsValid;
      }

      return triggerDueDateIsValid;
    }

    // For user tasks, we need to check task-specific validations
    //TODO: parallel steps support
    if (stepDefinition?.type === WorkflowStepType.PARALLEL) return true;
    if (!stepDefinition?.task.type) return false;

    if (stepDefinition.type === WorkflowStepType.USER_TASK) {
      switch (stepDefinition.task.type) {
        case UserTaskType.SUBMIT_FORM:
          return baseInformationIsValid && assigneeIsValid && submitFormIsValid;
        case UserTaskType.CHOICE:
          return baseInformationIsValid && assigneeIsValid && choiceIsValid;
        case UserTaskType.MANUAL_TASK:
        default:
          return baseInformationIsValid && assigneeIsValid;
      }
    }

    if (stepDefinition.type === WorkflowStepType.SYSTEM_ACTION) {
      switch (stepDefinition.task.type) {
        case SystemActionType.FORM_CHOICE:
          return baseInformationIsValid && formChoiceIsValid;
        case SystemActionType.VARIABLE_CHOICE:
          return baseInformationIsValid && variableChoiceIsValid;
        case SystemActionType.CREATE_FORM:
          return baseInformationIsValid && formCreationIsValid;
        case SystemActionType.DELAY:
          return baseInformationIsValid && delayIsValid;
        case SystemActionType.RUN_CALCULATIONS:
          return baseInformationIsValid && runCalculationsIsValid;
        case SystemActionType.CREATE_EVENT:
          return baseInformationIsValid && createEventIsValid;
        case SystemActionType.RUN_TEMPLATED_REPORT:
          return baseInformationIsValid && runTemplatedReportIsValid;
        case SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION:
          return baseInformationIsValid && runDRVCIsValid;
        case SystemActionType.LOCK_REPORT:
        case SystemActionType.UNLOCK_REPORT:
          return baseInformationIsValid && lockReportIsValid;
        case SystemActionType.LOCK_RECORD:
        case SystemActionType.UNLOCK_RECORD:
          return baseInformationIsValid && updateRecordStatusIsValid;
        case SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS:
          return (
            baseInformationIsValid && assessTemplatedReportForCommentsIsValid
          );
        case SystemActionType.UPDATE_CUSTOM_ATTRIBUTES:
        case SystemActionType.UPDATE_ATTRIBUTES:
          return baseInformationIsValid && updateAttributesIsValid;
        case SystemActionType.SEND_NOTIFICATION:
          return baseInformationIsValid && sendNotificationIsValid;
        case SystemActionType.SEND_REPORT:
          return baseInformationIsValid && sendReportIsValid;
        case SystemActionType.RUN_WORKER:
          return baseInformationIsValid && runWorkerIsValid;
        default:
          return baseInformationIsValid;
      }
    }

    return false;
  }, [
    stepDefinition,
    baseInformationIsValid,
    submitFormIsValid,
    choiceIsValid,
    assigneeIsValid,
    isTrigger,
    triggerDefinition,
    triggerCronIsValid,
    triggerDueDateIsValid,
    triggerWorkflowCompletionIsValid,
    triggerFormCreationIsValid,
    triggerAssetIsValid,
    formChoiceIsValid,
    formCreationIsValid,
    variableChoiceIsValid,
    delayIsValid,
    runCalculationsIsValid,
    createEventIsValid,
    runTemplatedReportIsValid,
    runDRVCIsValid,
    lockReportIsValid,
    updateRecordStatusIsValid,
    assessTemplatedReportForCommentsIsValid,
    updateAttributesIsValid,
    sendNotificationIsValid,
    sendReportIsValid,
    runWorkerIsValid,
  ]);

  const editableAutomations = [
    SystemActionType.FORM_CHOICE,
    SystemActionType.VARIABLE_CHOICE,
    SystemActionType.DELAY,
    SystemActionType.RUN_CALCULATIONS,
    SystemActionType.CREATE_EVENT,
    SystemActionType.RUN_TEMPLATED_REPORT,
    SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION,
    SystemActionType.LOCK_REPORT,
    SystemActionType.UNLOCK_REPORT,
    SystemActionType.LOCK_RECORD,
    SystemActionType.UNLOCK_RECORD,
    SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS,
    SystemActionType.UPDATE_CUSTOM_ATTRIBUTES,
    SystemActionType.UPDATE_ATTRIBUTES,
    SystemActionType.CREATE_FORM,
    SystemActionType.SEND_NOTIFICATION,
    SystemActionType.SEND_REPORT,
    SystemActionType.RUN_WORKER,
  ] as SystemActionTypeType[];

  const isStepEditable =
    isStepUserTask ||
    isStepTrigger ||
    (isStepSystemAction &&
      stepDefinition?.task.type &&
      editableAutomations.includes(stepDefinition?.task.type));

  const isEditing = isStepEditable && isInEditMode && canEditWorkflowTemplate;

  const areTherePendingChanges = isNodeDirty(selectedNodeId);

  const isRevertButtonDisabled = !areTherePendingChanges;

  const revertButton = (
    <ButtonWithPopover
      label={isRevertButtonDisabled ? "There are no changes to revert." : ""}
      icon="arrow-counter-clockwise"
      disabled={isRevertButtonDisabled}
      onClick={() => {
        setIsCancelConfirmationOpen(true);
      }}
      buttonProps={{ style: { width: "100%" } }}
      style={{ flexBasis: "50%" }}
    >
      Revert
    </ButtonWithPopover>
  );

  const doneButton = (
    <Button
      icon="check-circle"
      variant="primary"
      style={{ flexBasis: "50%" }}
      onClick={() => {
        onClose();

        const { getBaseInformation, getInput, getOutput, getPlugin } =
          useWorkflowConfigurationFormStore.getState();
        const baseInformationValues = getBaseInformation(isStepSystemAction);
        if (baseInformationValues)
          updateBaseInformationWithHistory(selectedNodeId, {
            name: baseInformationValues.name,
            description: baseInformationValues.description ?? "",
            ...(!isStepSystemAction &&
              "priority_id" in baseInformationValues && {
                priority_id: Number(baseInformationValues.priority_id ?? "0"),
              }),
            ...(!isStepSystemAction &&
              "is_dismissible" in baseInformationValues && {
                is_dismissible: baseInformationValues?.is_dismissible ?? false,
              }),
          });

        const inputValues = getInput();
        const outputValues = getOutput();

        if (inputValues) {
          updateInputWithHistory(selectedNodeId, inputValues);
        }

        if (outputValues) {
          updateOutputWithHistory(selectedNodeId, outputValues);
        }

        const pluginValues = getPlugin();
        if (pluginValues) {
          updatePluginWithHistory(selectedNodeId, pluginValues);
        }

        const { getAssignee } = useWorkflowConfigurationFormStore.getState();
        const assigneeValues = getAssignee();
        if (assigneeValues) {
          updateUserTaskAssigneeWithHistory(selectedNodeId, {
            // Values must be null to be able to "clear" them from the BE
            // i.e. when replacing an assignee_user with an assignee_group
            assignee_user: assigneeValues.assignee_user ?? null,
            assignee_group: assigneeValues.assignee_group ?? null,
            assignment_filter: assigneeValues.assignment_filter ?? null,
          });
        }

        if (
          stepDefinition?.type === WorkflowStepType.USER_TASK &&
          stepDefinition?.task.type === UserTaskType.SUBMIT_FORM
        ) {
          const { getSubmitForm } =
            useWorkflowConfigurationFormStore.getState();
          const submitFormValues = getSubmitForm();
          if (submitFormValues) {
            updateUserTaskSubmitFormWithHistory(
              selectedNodeId,
              submitFormValues
            );
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.USER_TASK &&
          stepDefinition?.task.type === UserTaskType.CHOICE
        ) {
          const { getChoice } = useWorkflowConfigurationFormStore.getState();
          const choiceValues = getChoice();
          if (choiceValues) {
            updateUserTaskChoiceWithHistory(selectedNodeId, choiceValues);
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.FORM_CHOICE
        ) {
          const { getFormChoice } =
            useWorkflowConfigurationFormStore.getState();
          const formChoiceValues = getFormChoice();
          if (formChoiceValues) {
            updateFormChoiceAutomationWithHistory(
              selectedNodeId,
              formChoiceValues
            );
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.VARIABLE_CHOICE
        ) {
          const { getVariableChoice } =
            useWorkflowConfigurationFormStore.getState();
          const variableChoiceValues = getVariableChoice();
          if (variableChoiceValues) {
            updateVariableChoiceAutomationWithHistory(
              selectedNodeId,
              variableChoiceValues
            );
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.RUN_TEMPLATED_REPORT
        ) {
          const { getRunTemplatedReport } =
            useWorkflowConfigurationFormStore.getState();
          const runTemplatedReportValues = getRunTemplatedReport();
          if (runTemplatedReportValues) {
            updateRunTemplatedReportWithHistory(
              selectedNodeId,
              runTemplatedReportValues
            );
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.RUN_WORKER
        ) {
          const { getRunWorker } = useWorkflowConfigurationFormStore.getState();
          const runWorkerValues = getRunWorker();
          if (runWorkerValues) {
            updateRunWorkerWithHistory(selectedNodeId, {
              worker_id: runWorkerValues.worker_id,
              worker_version_id: runWorkerValues.worker_version_id
                ? Number(runWorkerValues.worker_version_id)
                : undefined,
              worker_run_input: runWorkerValues.worker_run_input,
            });
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.CREATE_FORM
        ) {
          const { getFormCreation } =
            useWorkflowConfigurationFormStore.getState();
          const formCreationValues = getFormCreation();
          if (formCreationValues) {
            updateFormCreationAutomationWithHistory(
              selectedNodeId,
              formCreationValues
            );
          }
        }

        if (triggerDefinition) {
          const triggerIndex =
            getWorkflowConfigurationTriggerIndexFromTriggerKey(selectedNodeId);

          const { getTriggerDueDate } =
            useWorkflowConfigurationFormStore.getState();
          const triggerDueDateValues = getTriggerDueDate();

          updateTriggerDueDateWithHistory(
            triggerIndex,
            triggerDueDateValues?.duration ? triggerDueDateValues : undefined
          );

          if (isTriggerOtherWorkflowCompletion(triggerDefinition)) {
            const { getTriggerWorkflowCompletion } =
              useWorkflowConfigurationFormStore.getState();
            const triggerWorkflowCompletionValues =
              getTriggerWorkflowCompletion();
            if (triggerWorkflowCompletionValues) {
              updateTriggerWorkflowCompletionWithHistory(
                triggerIndex,
                triggerWorkflowCompletionValues
              );
            }
          }

          if (isTriggerFormSubmissionCreate(triggerDefinition)) {
            const { getTriggerFormCreation } =
              useWorkflowConfigurationFormStore.getState();
            const triggerFormCreationValues = getTriggerFormCreation();
            if (triggerFormCreationValues) {
              updateFormCreationTriggerWithHistory(triggerIndex, {
                answer_filter: triggerFormCreationValues.answer_filter,
                form_schema_id: triggerFormCreationValues.form_schema_id,
              });
            }
          }

          if (isTriggerCron(triggerDefinition)) {
            const { getTriggerCron } =
              useWorkflowConfigurationFormStore.getState();
            const triggerCronValues = getTriggerCron();
            if (triggerCronValues) {
              updateScheduledTriggerWithHistory(
                triggerIndex,
                triggerCronValues
              );
            }
          }

          if (
            isTriggerAssetCreate(triggerDefinition) ||
            isTriggerAssetUpdate(triggerDefinition)
          ) {
            const { getTriggerAsset } =
              useWorkflowConfigurationFormStore.getState();
            const triggerAssetValues = getTriggerAsset();
            if (triggerAssetValues) {
              updateAssetCreateAndUpdateTriggerWithHistory(
                triggerIndex,
                triggerAssetValues
              );
            }
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.DELAY
        ) {
          const { getDelay } = useWorkflowConfigurationFormStore.getState();
          const delayValues = getDelay();
          if (delayValues) {
            updateDelayWithHistory(selectedNodeId, delayValues);
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.RUN_CALCULATIONS
        ) {
          const { getRunCalculations } =
            useWorkflowConfigurationFormStore.getState();
          const runCalculationsValues = getRunCalculations();
          if (runCalculationsValues) {
            // Transform the form values back to the expected format
            let yearMonthsArray: string[] = [];
            let fillYearMonths = false;

            if (runCalculationsValues.month_mode === "specific") {
              // For specific months, use all values as-is and don't fill
              yearMonthsArray = runCalculationsValues.specific_months ?? [];
              fillYearMonths = false;
            } else if (runCalculationsValues.month_mode === "range") {
              // For range mode, create array with start and end (sorted), and set fill to true
              const startMonth = runCalculationsValues.start_month?.trim();
              const endMonth = runCalculationsValues.end_month?.trim();

              if (startMonth && endMonth) {
                // Sort the start and end months to ensure proper ordering
                yearMonthsArray = [startMonth, endMonth].sort();
              }
              fillYearMonths = true;
            }

            const filter = {
              entity_type: runCalculationsValues.entity_type,
              reporting_group_id: runCalculationsValues.reporting_group_id,
            };

            updateRunCalculationsWithHistory(selectedNodeId, {
              filter,
              year_months: yearMonthsArray,
              promote_to_record: runCalculationsValues.promote_to_record,
              fill_year_months: fillYearMonths,
            });
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.CREATE_EVENT
        ) {
          const { getCreateEvent } =
            useWorkflowConfigurationFormStore.getState();
          const createEventValues = getCreateEvent();
          if (createEventValues) {
            updateCreateEventWithHistory(selectedNodeId, createEventValues);
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          (stepDefinition?.task.type === SystemActionType.LOCK_REPORT ||
            stepDefinition?.task.type === SystemActionType.UNLOCK_REPORT)
        ) {
          const { getLockReport } =
            useWorkflowConfigurationFormStore.getState();
          const lockReportValues = getLockReport();
          if (lockReportValues) {
            updateLockReportWithHistory(selectedNodeId, lockReportValues);
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          (stepDefinition?.task.type === SystemActionType.LOCK_RECORD ||
            stepDefinition?.task.type === SystemActionType.UNLOCK_RECORD)
        ) {
          const { getUpdateRecordStatus } =
            useWorkflowConfigurationFormStore.getState();
          const updateRecordStatusValues = getUpdateRecordStatus();
          if (updateRecordStatusValues) {
            updateUpdateRecordStatusWithHistory(
              selectedNodeId,
              updateRecordStatusValues
            );
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type ===
            SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION
        ) {
          const { getRunDRVC } = useWorkflowConfigurationFormStore.getState();
          const runDRVCValues = getRunDRVC();
          if (runDRVCValues) {
            // Transform the form values back to the expected format
            let yearMonthsArray: string[] = [];
            let fillYearMonths = false;

            if (runDRVCValues.month_mode === "specific") {
              // For specific months, use all values as-is and don't fill
              yearMonthsArray = runDRVCValues.specific_months ?? [];
              fillYearMonths = false;
            } else if (runDRVCValues.month_mode === "range") {
              // For range mode, create array with start and end (sorted), and set fill to true
              const startMonth = runDRVCValues.start_month?.trim();
              const endMonth = runDRVCValues.end_month?.trim();

              if (startMonth && endMonth) {
                // Sort the start and end months to ensure proper ordering
                yearMonthsArray = [startMonth, endMonth].sort();
              }
              fillYearMonths = true;
            }

            updateRunDRVCWithHistory(selectedNodeId, {
              filter: {
                ...runDRVCValues.filter,
                year_month: {
                  $in: yearMonthsArray,
                },
              },
              overwrite_all_values: runDRVCValues.overwrite_all_values,
              seed_records: runDRVCValues.seed_records,
              fill_year_months: fillYearMonths,
            });
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type ===
            SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS
        ) {
          const { getAssessTemplatedReportForComments } =
            useWorkflowConfigurationFormStore.getState();
          const assessTemplatedReportForCommentsValues =
            getAssessTemplatedReportForComments();
          if (assessTemplatedReportForCommentsValues) {
            updateAssessTemplatedReportForCommentsWithHistory(
              selectedNodeId,
              assessTemplatedReportForCommentsValues
            );
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          (stepDefinition?.task.type === SystemActionType.UPDATE_ATTRIBUTES ||
            stepDefinition?.task.type ===
              SystemActionType.UPDATE_CUSTOM_ATTRIBUTES)
        ) {
          const { getUpdateAttributes } =
            useWorkflowConfigurationFormStore.getState();
          const updateAttributesValues = getUpdateAttributes();
          if (updateAttributesValues) {
            updateUpdateAttributesWithHistory(
              selectedNodeId,
              updateAttributesValues
            );
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.SEND_NOTIFICATION
        ) {
          const { getSendNotification } =
            useWorkflowConfigurationFormStore.getState();
          const sendNotificationValues = getSendNotification();
          if (sendNotificationValues) {
            updateSendNotificationWithHistory(
              selectedNodeId,
              sendNotificationValues
            );
          }
        }

        if (
          stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION &&
          stepDefinition?.task.type === SystemActionType.SEND_REPORT
        ) {
          const { getSendReport } =
            useWorkflowConfigurationFormStore.getState();
          const sendReportValues = getSendReport();
          if (sendReportValues) {
            updateSendReportWithHistory(selectedNodeId, sendReportValues);
          }
        }
      }}
      disabled={!areAllFormsValid}
    >
      Done
    </Button>
  );

  const drawerActionRow: DrawerProps["actionRow"] = isEditing
    ? [revertButton, doneButton]
    : undefined;

  const drawerProps: Omit<
    DrawerProps,
    "isOpen" | "onClose" | "children" | "actionRow"
  > = useMemo(() => {
    return {
      colorVariant: isTrigger
        ? isEditing
          ? "stone"
          : "grey"
        : shouldHaveAutomationVariant
          ? "teal"
          : "stone",
      title: isTrigger
        ? `${isEditing ? "Configure " : ""}Workflow Trigger`
        : isStepParallel
          ? "Parallel Branches"
          : shouldHaveAutomationVariant
            ? `${isEditing ? "Configure " : ""}Automation Details`
            : `${isEditing ? "Configure " : ""}User Task Details`,
      iconActionLeft: shouldHaveAutomationVariant ? (
        <Icon
          variant="sparkle"
          weight="fill"
        />
      ) : undefined,
    };
  }, [shouldHaveAutomationVariant, isTrigger, isStepParallel, isEditing]);

  const descriptorProps: DrawerDescriptorWithIconProps = useMemo(() => {
    let icon: IconVariants = "validere";
    let title = "";
    let titleHeader = "";

    if (stepDefinition) {
      icon = getWorkflowTaskIconFromType(
        stepDefinition?.type === WorkflowStepType.PARALLEL
          ? WorkflowStepType.PARALLEL
          : stepDefinition?.task?.type
      );
      title = stepDefinition.name;
      titleHeader = getWorkflowTaskNameFromType(
        stepDefinition?.type === WorkflowStepType.PARALLEL
          ? WorkflowStepType.PARALLEL
          : stepDefinition?.task?.type
      );
    }

    if (selectedNode?.data.variant === "trigger" && triggerDefinition) {
      icon = getWorkflowTriggerIconFromTrigger(triggerDefinition);
      title = getWorkflowTriggerNameFromTrigger(triggerDefinition);
      titleHeader = getWorkflowTriggerNameFromTrigger(triggerDefinition);

      if (isTriggerAssetCreate(triggerDefinition)) {
        title = localize(`${triggerDefinition.asset_type} Creation`);
      }
      if (isTriggerAssetUpdate(triggerDefinition)) {
        title = localize(`${triggerDefinition.asset_type} Update`);
      }
      if (isTriggerOtherWorkflowCompletion(triggerDefinition)) {
        title = "Other Workflow Completion Trigger";
      }
      if (isTriggerFormSubmissionCreate(triggerDefinition)) {
        if (formSchemaQuery?.data?.data) {
          title = `"${formSchemaQuery?.data?.data.name}" Submission`;
        }
      }
      if (isTriggerCron(triggerDefinition)) {
        title = "Scheduled Trigger";
      }
    }

    if (selectedNode?.data.variant === "finish") {
      icon = "seal-check";
      title = "Workflow Complete";
    }

    const titleWithEditedChip: ReactElement[] = [];

    if (title) titleWithEditedChip.push(<>{title}</>);

    if (areTherePendingChanges) {
      titleWithEditedChip.push(
        <Pill
          variant="info"
          hasDot
        >
          Edited
        </Pill>
      );
    }

    return {
      title: titleWithEditedChip,
      titleHeader,
      iconProps: {
        variant: icon,
        containerClassName: cx("descriptorIcon"),
      },
    };
  }, [
    stepDefinition,
    selectedNode,
    formSchemaQuery?.data?.data,
    areTherePendingChanges,
  ]);

  if (isStepParallel) return null;

  return (
    <Drawer
      isOpen={!!selectedNode}
      onClose={onClose}
      size="md"
      actionRow={drawerActionRow}
      mode="non-blocking"
      {...drawerProps}
    >
      <div className={cx("contentContainer")}>
        <Drawer.DescriptorWithIcon {...descriptorProps} />

        {/* Preview Mode */}
        {!isEditing && !isStepParallel && (
          <>
            <WorkflowConfigurationDrawerTaskDetails
              panelTitle={
                isStepParallel
                  ? "Parallel Branches"
                  : shouldHaveAutomationVariant
                    ? "Automation Overview"
                    : "Task Overview"
              }
              stepDefinition={stepDefinition}
              stepId={selectedNodeId}
              workflowTemplate={workflowTemplate}
            />

            <WorkflowConfigurationDrawerTriggerDetails
              triggerDefinition={triggerDefinition}
            />
            {isStepUserTask ? (
              <WorkflowConfigurationDrawerAssignmentFilterDetails
                stepDefinition={stepDefinition}
              />
            ) : null}
          </>
        )}

        {/* Edit Mode */}
        {isEditing && (
          <>
            {isStepUserTask && (
              <>
                <ButtonToggleGroup
                  name="content-switcher"
                  items={contentSwitcherItems}
                  onChange={(value) =>
                    setActiveTab(value as WorkflowTemplateEditTabsType)
                  }
                  activeKey={activeTab}
                />

                {activeTab === "details" && (
                  <div className={cx("contentContainer")}>
                    <WorkflowConfigurationBaseInformation
                      stepId={selectedNodeId}
                      isStepSystemAction={isStepSystemAction}
                    />

                    {stepDefinition.task.type === UserTaskType.SUBMIT_FORM && (
                      <WorkflowConfigurationSubmitForm
                        taskDefinition={stepDefinition.task}
                      />
                    )}

                    {stepDefinition.task.type === UserTaskType.CHOICE && (
                      <WorkflowConfigurationChoice />
                    )}
                  </div>
                )}

                {activeTab === "assignees" && (
                  <WorkflowConfigurationAssignee
                    stepDefinition={stepDefinition}
                    assetType={workflowTemplate?.assetType}
                    stepId={selectedNodeId}
                  />
                )}

                {activeTab === "advanced" && (
                  <Accordion defaultActiveKeys={["input", "output", "plugin"]}>
                    {shouldRenderInputOutputConfig && (
                      <Accordion.AccordionPanel
                        title="Pre-Filled Answers"
                        dataKey="input"
                      >
                        {stepDefinition.task.type === "submit_form" && (
                          <WorkflowConfigurationInputVariableForm
                            stepDefinition={stepDefinition.task}
                            selectedNodeId={selectedNodeId}
                          />
                        )}
                      </Accordion.AccordionPanel>
                    )}
                    {shouldRenderInputOutputConfig && (
                      <Accordion.AccordionPanel
                        title="Output Variables"
                        dataKey="output"
                      >
                        {stepDefinition.task.type === "submit_form" && (
                          <WorkflowConfigurationOutputVariableForm
                            stepDefinition={stepDefinition.task}
                            selectedNodeId={selectedNodeId}
                          />
                        )}
                      </Accordion.AccordionPanel>
                    )}
                    <Accordion.AccordionPanel
                      title="Plugin"
                      dataKey="plugin"
                    >
                      <WorkflowConfigurationPluginOptions />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}
              </>
            )}
            {isStepTrigger && (
              <>
                {isTriggerOtherWorkflowCompletion(triggerDefinition) && (
                  <WorkflowConfigurationTriggerWorkflowCompletion />
                )}
                {(isTriggerAssetCreate(triggerDefinition) ||
                  isTriggerAssetUpdate(triggerDefinition)) && (
                  <WorkflowConfigurationAssetCreateUpdate
                    hasAttributesPicker={isTriggerAssetUpdate(
                      triggerDefinition
                    )}
                    triggerIndex={getWorkflowConfigurationTriggerIndexFromTriggerKey(
                      selectedNodeId
                    )}
                  />
                )}

                {isTriggerCron(triggerDefinition) && (
                  <WorkflowScheduledTriggerForm
                    triggerDefinition={triggerDefinition}
                  />
                )}

                <WorkflowConfigurationTriggerDueDate />

                {isTriggerFormSubmissionCreate(triggerDefinition) && (
                  <WorkflowConfigurationTriggerFormCreation
                    triggerDefinition={triggerDefinition}
                  />
                )}
              </>
            )}

            {isStepSystemAction && (
              <>
                {stepDefinition.task.type === SystemActionType.CREATE_EVENT && (
                  <Accordion defaultActiveKeys={["overview", "event-details"]}>
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Event Details"
                      dataKey="event-details"
                    >
                      <WorkflowConfigurationEventDetails />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type === SystemActionType.FORM_CHOICE && (
                  <Accordion defaultActiveKeys={["overview", "form-details"]}>
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Form Details"
                      dataKey="form-details"
                    >
                      <WorkflowConfigurationFormChoiceDetails />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type ===
                  SystemActionType.VARIABLE_CHOICE && (
                  <Accordion
                    defaultActiveKeys={["overview", "variable-choice-details"]}
                  >
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Variable Choice Details"
                      dataKey="variable-choice-details"
                    >
                      <WorkflowConfigurationVariableChoiceDetails
                        stepId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                    {isPluginEnabled && (
                      <Accordion.AccordionPanel
                        title="Plugin"
                        dataKey="plugin"
                      >
                        <WorkflowConfigurationPluginOptions />
                      </Accordion.AccordionPanel>
                    )}
                  </Accordion>
                )}

                {stepDefinition.task.type === SystemActionType.DELAY && (
                  <Accordion defaultActiveKeys={["overview", "delay-details"]}>
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Delay Details"
                      dataKey="delay-details"
                    >
                      <WorkflowConfigurationDelayDetails />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type === SystemActionType.CREATE_FORM && (
                  <WorkflowConfigurationFormCreationDetails
                    selectedNodeId={selectedNodeId}
                  />
                )}

                {(stepDefinition.task.type === SystemActionType.LOCK_RECORD ||
                  stepDefinition.task.type ===
                    SystemActionType.UNLOCK_RECORD) && (
                  <Accordion
                    defaultActiveKeys={[
                      "overview",
                      "update-record-status-details",
                    ]}
                  >
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      dataKey="update-record-status-details"
                      title={
                        stepDefinition.task.type ===
                        SystemActionType.LOCK_RECORD
                          ? "Locked Record Details"
                          : "Unlocked Record Details"
                      }
                    >
                      <WorkflowConfigurationUpdateRecordStatusDetails
                        stepId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {(stepDefinition.task.type === SystemActionType.LOCK_REPORT ||
                  stepDefinition.task.type ===
                    SystemActionType.UNLOCK_REPORT) && (
                  <Accordion defaultActiveKeys={["overview", "report-details"]}>
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>

                    <Accordion.AccordionPanel
                      title={`${stepDefinition.task.type === SystemActionType.LOCK_REPORT ? "Lock" : "Unlock"} Report Details`}
                      dataKey="report-details"
                    >
                      <WorkflowConfigurationLockReportDetails
                        stepId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type ===
                  SystemActionType.RUN_CALCULATIONS && (
                  <Accordion
                    defaultActiveKeys={["overview", "calculations-details"]}
                  >
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Calculation Details"
                      dataKey="calculations-details"
                    >
                      <WorkflowConfigurationRunCalculationsDetails
                        selectedNodeId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type ===
                  SystemActionType.RUN_TEMPLATED_REPORT && (
                  <Accordion defaultActiveKeys={["overview", "report-details"]}>
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Report Details"
                      dataKey="report-details"
                    >
                      <WorkflowConfigurationRunTemplatedReportDetails
                        stepId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type === SystemActionType.RUN_WORKER && (
                  <Accordion defaultActiveKeys={["overview", "worker-details"]}>
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Worker Details"
                      dataKey="worker-details"
                    >
                      <WorkflowConfigurationRunWorkerDetails
                        stepId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type ===
                  SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION && (
                  <Accordion defaultActiveKeys={["overview", "drvc-details"]}>
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Configuration Details"
                      dataKey="drvc-details"
                    >
                      <WorkflowConfigurationRunDRVCDetails
                        stepId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type ===
                  SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS && (
                  <Accordion
                    defaultActiveKeys={[
                      "overview",
                      "assess-templated-report-for-comments-details",
                    ]}
                  >
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>

                    <Accordion.AccordionPanel
                      dataKey="assess-templated-report-for-comments-details"
                      title="Report Details"
                    >
                      <WorkflowConfigurationAssessTemplatedReportForComments
                        stepId={selectedNodeId}
                        workflowTemplate={workflowTemplate}
                      />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}
                {(stepDefinition.task.type ===
                  SystemActionType.UPDATE_ATTRIBUTES ||
                  stepDefinition.task.type ===
                    SystemActionType.UPDATE_CUSTOM_ATTRIBUTES) && (
                  <Accordion defaultActiveKeys={["overview", "asset-details"]}>
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Asset Details"
                      dataKey="asset-details"
                    >
                      <WorkflowConfigurationUpdateAttributesDetails />
                    </Accordion.AccordionPanel>
                  </Accordion>
                )}

                {stepDefinition.task.type ===
                  SystemActionType.SEND_NOTIFICATION && (
                  <Accordion
                    defaultActiveKeys={[
                      "overview",
                      "send-notification-details",
                    ]}
                  >
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Send Notification Details"
                      dataKey="send-notification-details"
                    >
                      <WorkflowConfigurationSendNotificationDetails
                        stepId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                    {isPluginEnabled && (
                      <Accordion.AccordionPanel
                        title="Plugin"
                        dataKey="plugin"
                      >
                        <WorkflowConfigurationPluginOptions />
                      </Accordion.AccordionPanel>
                    )}
                  </Accordion>
                )}

                {stepDefinition.task.type === SystemActionType.SEND_REPORT && (
                  <Accordion
                    defaultActiveKeys={["overview", "send-report-details"]}
                  >
                    <Accordion.AccordionPanel
                      title="Overview"
                      dataKey="overview"
                    >
                      <WorkflowConfigurationBaseInformation
                        stepId={selectedNodeId}
                        isStepSystemAction
                      />
                    </Accordion.AccordionPanel>
                    <Accordion.AccordionPanel
                      title="Send Report Details"
                      dataKey="send-report-details"
                    >
                      <WorkflowConfigurationSendReportDetails
                        stepId={selectedNodeId}
                      />
                    </Accordion.AccordionPanel>
                    {isPluginEnabled && (
                      <Accordion.AccordionPanel
                        title="Plugin"
                        dataKey="plugin"
                      >
                        <WorkflowConfigurationPluginOptions />
                      </Accordion.AccordionPanel>
                    )}
                  </Accordion>
                )}
              </>
            )}
          </>
        )}

        {isFinishNode && <WorkflowConfigurationDrawerFinishedDetails />}

        <WorkflowConfigurationDialog
          isOpen={isCancelConfirmationOpen}
          onClose={() => {
            setIsCancelConfirmationOpen(false);
          }}
          onConfirm={() => {
            revertTaskChanges(selectedNodeId);
            onClose();
          }}
          buttonLabel="Revert Edits"
          buttonVariant="error"
          title="Revert Edits"
          message="Reverting edits on this node will cancel
            all changes made to the node in this session.
            This action cannot be undone."
        />
      </div>
    </Drawer>
  );
};

export default WorkflowConfigurationDrawer;
